import React, { useEffect, useState, useMemo } from "react";
import {
  TextField, Grid, MenuItem, Button, Typography, Box,
  CardContent, Paper, CircularProgress
} from "@mui/material";
import axios from "axios";
import baseUrl from "../../config/baseUrl";
import Lottie from "lottie-react";
import successAnimation from "./successAnimation.json";
import failed from "./failed.json";

const CURRENCIES = [{ label: "INR", value: "INR" }];

const generateTransactionId = (mer) => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const randomPart = Array.from({ length: 8 }, () => characters[Math.floor(Math.random() * characters.length)]).join('');
  const timestamp = Date.now().toString(); // milliseconds since epoch
  return `${mer}${timestamp}${randomPart}`;
};


const Singlepayout = () => {
  const mer = sessionStorage.getItem("mer");
  const auth = sessionStorage.getItem("user");

  const [form, setForm] = useState({
    transaction_id: generateTransactionId(mer),
    currency: "",
    amount: "",
    txn_type: "",
    bankcode: "",
    account_name: "",
    email: "",
    phoneNumber: "",
    account_no: "",
    ifsc_code: "",
    address: "",
    city: "",
    pincode: "",
    province: "",
    notes: "Payout Payment",
    googleOtp: "",
  });

  const [bankcodes, setBankcodes] = useState([]);
  const [wallet, setWallet] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showMeme, setShowMeme] = useState(false);
  const [showFailed, setShowFailed] = useState(false);

  const config = useMemo(() => ({
    headers: {
      "Content-type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${auth}`,
    },
  }), [auth]);

  const walletBalance = Number(wallet);
  const isAmountInvalid = Number(form.amount) > walletBalance;

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const fetchBankCodes = async () => {
    try {
      const res = await axios.post(`${baseUrl}/singlePayoutBankcodes`, { currency: form.currency }, config);
      setBankcodes(res?.data.data || []);
      setWallet(res?.data.wallet || 0);
    } catch (error) {
      console.error("Error fetching bank codes:", error);
    }
  };

  useEffect(() => {
    if (form.currency) fetchBankCodes();
  }, [form.currency, config]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await axios.post(`${baseUrl}/singlePayoutPayment`, form, config);

      if (result.status === 200) {
        result.data?.[0]?.status === "SUCCESS" ? setShowMeme(true) : setShowFailed(true);
      } else {
        alert(result.data?.message || "Unexpected error");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showMeme || showFailed) {
        window.location.reload();
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [showMeme, showFailed]);

  return (
    <CardContent sx={{ position: "relative" }}>
      {/* Form */}
      <Box sx={{ filter: showMeme || showFailed ? "blur(4px)" : "none", pointerEvents: showMeme || showFailed ? "none" : "auto" }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, backgroundColor: "#f9f9f9" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1976d2", fontSize: "1.5rem", mb: 2, textTransform: "uppercase" }}>
            Single Payout Form
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Transaction ID" value={form.transaction_id} name="transaction_id" disabled />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField select fullWidth label="Currency" name="currency" value={form.currency} onChange={handleChange}>
                <MenuItem disabled>SELECT CURRENCY</MenuItem>
                {CURRENCIES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField select fullWidth label="Bank Code" name="bankcode" value={form.bankcode} onChange={handleChange}>
                <MenuItem disabled>Select Bankcodes</MenuItem>
                {bankcodes.length ? bankcodes.map((b) => (
                  <MenuItem key={b.gatewayNo} value={b.gatewayNo}>{b.gateway_name}</MenuItem>
                )) : <MenuItem disabled>No Bank Assigned</MenuItem>}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField select fullWidth label="Transaction Type" name="txn_type" value={form.txn_type} onChange={handleChange}>
                <MenuItem disabled>SELECT TYPE</MenuItem>
                {["IMPS", "RTGS", "NEFT"].map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Amount" name="amount" type="number" value={form.amount} onChange={handleChange}
                disabled={!form.currency} error={isAmountInvalid}
                helperText={isAmountInvalid ? `Exceeds wallet: ₹${walletBalance}` : ""} />
            </Grid>

            {[
              ["Account Name", "account_name"],
              ["Account Number", "account_no"],
              ["IFSC Code", "ifsc_code"],
              ["Email", "email"],
              ["Phone Number", "phoneNumber"],
              ["Address", "address"],
              ["City", "city"],
              ["State", "province"],
              ["Pincode", "pincode"],
              ["Google Authenticator Code", "googleOtp", { maxLength: 6 }],
            ].map(([label, name, extra], i) => (
              <Grid item xs={12} sm={3} key={name}>
                <TextField fullWidth label={label} name={name} value={form[name]} onChange={handleChange} {...extra} />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField fullWidth label="Notes" name="notes" value={form.notes} onChange={handleChange} multiline rows={3} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Notes: IMPS is instant transfer 24×7 up to ₹2,00,000
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isAmountInvalid || loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}>
                {loading ? "Processing..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Result Animation */}
      {[showMeme, showFailed].some(Boolean) && (
        <Box sx={{
          position: "absolute", top: 0, left: 0, zIndex: 10, width: "100%", height: "100%",
          backgroundColor: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center",
          justifyContent: "center", flexDirection: "column", backdropFilter: "blur(3px)",
        }}>
          <Typography variant="h5" sx={{ mb: 2, color: showMeme ? "#4caf50" : "red" }}>
            {showMeme ? "Payout Successful 🎉" : "Payout Failed"}
          </Typography>
          <Lottie animationData={showMeme ? successAnimation : failed} loop={false} style={{ width: 250, height: 250 }} />
        </Box>
      )}
    </CardContent>
  );
};

export default Singlepayout;

